import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { FaPlus, FaEdit, FaTrash, FaRupeeSign, FaFilter } from 'react-icons/fa';
import api from '../services/api';
import { normalizeListForAdmin, toBool } from '../utils/normalizeApi';

function FeesManagement() {
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterClass, setFilterClass] = useState('All');
  
  const [formData, setFormData] = useState({
    className: '',
    feeType: 'Tuition',
    amount: '',
    frequency: 'Monthly',
    description: '',
    isActive: true
  });

  const classOptions = ['Pre-Nursery', 'Nursery', 'KG', 'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 
                        'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12'];
  
  const feeTypeOptions = ['Tuition', 'Admission', 'Annual', 'Transport', 'Hostel', 'Exam', 'Library'];
  const frequencyOptions = ['Monthly', 'Quarterly', 'Half-Yearly', 'Yearly', 'One-Time'];

  useEffect(() => { fetchFees(); }, []);

  const fetchFees = async () => {
    try {
      const response = await api.get('/fees');
      console.log('💰 Fetched fees:', response.data);
      setFees(normalizeListForAdmin(response.data));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching fees:', error);
      setFees([
        { id: 1, className: 'Class 10', feeType: 'Tuition', amount: 1500, frequency: 'Monthly', description: 'Monthly tuition fee', isActive: true }
      ]);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/fees/${editingId}`, formData);
        alert('✅ Fee structure updated successfully!');
      } else {
        await api.post('/fees', formData);
        alert('✅ Fee structure added successfully!');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      fetchFees();
    } catch (error) {
      console.error('Fee CRUD Error:', error);
      alert('❌ Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (item) => {
    console.log('📝 Editing fee:', item);
    setEditingId(item.id);
    setFormData({
      className: item.className || '',
      feeType: item.feeType || 'Tuition',
      amount: item.amount || '',
      frequency: item.frequency || 'Monthly',
      description: item.description || '',
      isActive: toBool(item.isActive, true)
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this fee structure?')) return;
    try {
      await api.delete(`/fees/${id}`);
      alert('✅ Fee deleted successfully!');
      fetchFees();
    } catch (error) {
      console.error('Delete error:', error);
      alert('❌ Error deleting fee');
    }
  };

  const resetForm = () => {
    setFormData({ className: '', feeType: 'Tuition', amount: '', frequency: 'Monthly', description: '', isActive: true });
  };

  const filteredFees = filterClass === 'All' ? fees : fees.filter(fee => fee.className === filterClass);
  
  const totalAmount = filteredFees.reduce((sum, fee) => sum + parseFloat(fee.amount || 0), 0);

  if (loading) return <AdminLayout><div className="p-6">Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-6">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-blue-900">Fee Structure Management</h1>
            <p className="text-gray-600 mt-1">Manage school fees for all classes</p>
          </div>
          <button onClick={() => setShowForm(true)}
            className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition flex items-center gap-2 shadow-lg">
            <FaPlus /> Add Fee Structure
          </button>
        </motion.div>

        {/* Filter and Summary */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex items-center gap-2">
              <FaFilter className="text-blue-900" />
              <label className="font-medium text-gray-700">Filter by Class:</label>
              <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-900">
                <option value="All">All Classes</option>
                {classOptions.map(cls => <option key={cls} value={cls}>{cls}</option>)}
              </select>
            </div>
            <div className="bg-green-50 px-6 py-3 rounded-lg border border-green-200">
              <span className="text-sm text-gray-600">Total Amount: </span>
              <span className="text-2xl font-bold text-green-700">₹{totalAmount.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>

        {/* Fees Grid */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredFees.map((item) => (
            <div key={item.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
              <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-lg">{item.className}</h3>
                    <p className="text-blue-100 text-sm">{item.feeType} Fee</p>
                  </div>
                  <FaRupeeSign className="text-3xl opacity-75" />
                </div>
              </div>
              <div className="p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Amount:</span>
                  <span className="text-2xl font-bold text-blue-900">₹{parseFloat(item.amount).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Frequency:</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">{item.frequency}</span>
                </div>
                {item.description && (
                  <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                )}
                <div className="flex justify-between items-center pt-3 border-t">
                  <span className={`px-2 py-1 rounded text-xs ${item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {item.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <div className="flex gap-2">
                    <button onClick={() => handleEdit(item)} className="text-blue-900 hover:text-blue-700 transition"><FaEdit size={18} /></button>
                    <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800 transition"><FaTrash size={18} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </motion.div>

        {filteredFees.length === 0 && (
          <div className="text-center text-gray-600 mt-12 py-8">
            <p className="text-xl">No fee structures found</p>
            <p className="text-sm mt-2">Click "Add Fee Structure" to create one</p>
          </div>
        )}

        {/* Modal Form */}
        {showForm && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">{editingId ? 'Edit' : 'Add'} Fee Structure</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Class *</label>
                    <select value={formData.className} onChange={(e) => setFormData({ ...formData, className: e.target.value })} required
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900">
                      <option value="">Select Class</option>
                      {classOptions.map(cls => <option key={cls} value={cls}>{cls}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fee Type *</label>
                    <select value={formData.feeType} onChange={(e) => setFormData({ ...formData, feeType: e.target.value })} required
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900">
                      {feeTypeOptions.map(type => <option key={type} value={type}>{type}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Amount (₹) *</label>
                    <input type="number" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="1500" min="0" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Frequency *</label>
                    <select value={formData.frequency} onChange={(e) => setFormData({ ...formData, frequency: e.target.value })} required
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900">
                      {frequencyOptions.map(freq => <option key={freq} value={freq}>{freq}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3" className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900 resize-none"
                    placeholder="Brief description of this fee"></textarea>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-5 h-5 text-blue-900 rounded focus:ring-blue-900" />
                  <label className="text-sm text-gray-700">Active</label>
                </div>
                <div className="flex gap-4 pt-4">
                  <button type="submit" className="flex-1 bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-800 transition font-semibold">
                    {editingId ? 'Update' : 'Add'} Fee Structure
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
                    className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition font-semibold">Cancel</button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </div>
    </AdminLayout>
  );
}

export default FeesManagement;
